/* eslint max-lines: ["error", { "max": 850 }] */
/* eslint-disable no-console */
import dayjs, { Dayjs } from 'dayjs';
import businessTime from 'dayjs-business-time';
import dayjsCalendar from 'dayjs/plugin/calendar';
import dayjsCustomParseFormat from 'dayjs/plugin/customParseFormat';
import dayjsIsBetween from 'dayjs/plugin/isBetween';
import dayjsIsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import dayjsIsSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import dayjsLocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjsMinMax from 'dayjs/plugin/minMax';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import dayjsTimezone from 'dayjs/plugin/timezone';
import dayjsUTC from 'dayjs/plugin/utc';

import {
  DATE_TIME_NO_TZ_FORMAT,
  DAY_MONTH_DATE_FORMAT,
  EN_LOCALE_DATE_FORMAT,
  EN_LOCALE_DATE_FORMAT_NO_LEADING_ZERO,
  EN_LOCALE_DATE_SHORT_YEAR_FORMAT,
  EN_LOCALE_DATE_TIME_FORMAT,
  EN_LOCALE_LONG_DATE_FORMAT,
  EN_LOCALE_LONG_MONTH_YEAR_FORMAT,
  ISO_DATE_FORMAT,
  ISO_DATE_TIME_FORMAT,
  ISO_MONTH_FORMAT,
  MONTH_DATE_FORMAT,
  MONTH_DAY_EN_LOCALE_DATE_FORMAT,
  NATIVE_DATE_ISO_DATE_TIME_FORMAT,
  PERMISSIVE_EN_LOCALE_DATE_FORMATS_ARRAY,
} from './date.consts';
import { toISOString } from './date.utils';

/**
 * US Federal Holidays (YYYY-MM-DD). Includes observed dates when holidays fall on weekends.
 * Source: https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/ + https://www.timeanddate.com/holidays/us/2035
 * Covers 2025–2035.
 */
export const US_FEDERAL_HOLIDAYS = [
  '2025-01-01',
  '2025-01-20',
  '2025-02-17',
  '2025-05-26',
  '2025-06-19',
  '2025-07-04',
  '2025-09-01',
  '2025-10-13',
  '2025-11-11',
  '2025-11-27',
  '2025-12-25',
  '2026-01-01',
  '2026-01-19',
  '2026-02-16',
  '2026-05-25',
  '2026-06-19',
  '2026-07-03',
  '2026-07-04',
  '2026-09-07',
  '2026-10-12',
  '2026-11-11',
  '2026-11-26',
  '2026-12-25',
  '2027-01-01',
  '2027-01-18',
  '2027-02-15',
  '2027-05-31',
  '2027-06-18',
  '2027-06-19',
  '2027-07-05',
  '2027-07-04',
  '2027-09-06',
  '2027-10-11',
  '2027-11-11',
  '2027-11-25',
  '2027-12-24',
  '2027-12-25',
  '2028-01-01',
  '2028-01-17',
  '2028-02-21',
  '2028-05-29',
  '2028-06-19',
  '2028-07-04',
  '2028-09-04',
  '2028-10-09',
  '2028-11-10',
  '2028-11-11',
  '2028-11-23',
  '2028-12-25',
  '2029-01-01',
  '2029-01-15',
  '2029-02-19',
  '2029-05-28',
  '2029-06-19',
  '2029-07-04',
  '2029-09-03',
  '2029-10-08',
  '2029-11-12',
  '2029-11-11',
  '2029-11-22',
  '2029-12-25',
  '2030-01-01',
  '2030-01-21',
  '2030-02-18',
  '2030-05-27',
  '2030-06-19',
  '2030-07-04',
  '2030-09-02',
  '2030-10-14',
  '2030-11-11',
  '2030-11-28',
  '2030-12-25',
  '2031-01-01',
  '2031-01-20',
  '2031-02-17',
  '2031-05-26',
  '2031-06-19',
  '2031-07-04',
  '2031-09-01',
  '2031-10-13',
  '2031-11-11',
  '2031-11-27',
  '2031-12-25',
  '2032-01-01',
  '2032-01-19',
  '2032-02-16',
  '2032-05-31',
  '2032-06-18',
  '2032-06-19',
  '2032-07-05',
  '2032-07-04',
  '2032-09-06',
  '2032-10-11',
  '2032-11-11',
  '2032-11-25',
  '2032-12-24',
  '2032-12-25',
  '2033-01-01',
  '2033-01-17',
  '2033-02-21',
  '2033-05-30',
  '2033-06-20',
  '2033-06-19',
  '2033-07-04',
  '2033-09-05',
  '2033-10-10',
  '2033-11-11',
  '2033-11-24',
  '2033-12-26',
  '2033-12-25',
  '2034-01-02',
  '2034-01-01',
  '2034-01-16',
  '2034-02-20',
  '2034-05-29',
  '2034-06-19',
  '2034-07-04',
  '2034-09-04',
  '2034-10-09',
  '2034-11-10',
  '2034-11-11',
  '2034-11-23',
  '2034-12-25',
  '2035-01-01',
  '2035-01-15',
  '2035-02-19',
  '2035-05-28',
  '2035-06-19',
  '2035-07-04',
  '2035-09-03',
  '2035-10-08',
  '2035-11-12',
  '2035-11-11',
  '2035-11-22',
  '2035-12-25',
];

/**
 * Sets the holidays for business day calculations.
 * @param holidays Array of ISO date strings (YYYY-MM-DD)
 */
export function setHolidays(holidays: string[]): void {
  // eslint-disable-next-line no-restricted-syntax
  const nowYear = dayjs().year();
  if (nowYear >= 2035) {
    console.error('US_FEDERAL_HOLIDAYS const needs to be updated.');
  }

  dayjs.setHolidays?.(holidays);
}

export function configureDayjs() {
  dayjs.extend(dayjsUTC);
  dayjs.extend(dayjsTimezone);
  dayjs.extend(dayjsLocalizedFormat);
  dayjs.extend(dayjsCustomParseFormat);
  dayjs.extend(dayjsIsSameOrBefore);
  dayjs.extend(dayjsIsSameOrAfter);
  dayjs.extend(dayjsIsBetween);
  dayjs.extend(dayjsMinMax);
  dayjs.extend(dayjsRelativeTime);
  dayjs.extend(dayjsCalendar);
  dayjs.extend(businessTime);

  dayjs.locale('en');
  setHolidays(US_FEDERAL_HOLIDAYS);
  // dayjs.tz.setDefault(TZ_PST8PDT);
}

configureDayjs();

export type DateUtilInstance = Dayjs;
export type DateInput = Date | string | Dayjs;

/*
 Basic
 *********************************************************/

export function getDateUtil(): typeof dayjs {
  return dayjs;
}

export function getNow(): DateUtilInstance {
  const dateUtil = getDateUtil();
  return dateUtil();
}

export function getToday(): DateUtilInstance {
  return getNow().startOf('day');
}

export function getNowAsDate(): Date {
  return getNow().toDate();
}

/*
 Parse
 *********************************************************/

export function parseDateEnLocale(date: string): DateUtilInstance {
  const dateUtil = getDateUtil();
  return dateUtil(date, EN_LOCALE_DATE_FORMAT, true);
}

export function parseDateTimeEnLocale(date: string): DateUtilInstance {
  const dateUtil = getDateUtil();
  return dateUtil(date, EN_LOCALE_DATE_TIME_FORMAT, true);
}

/* ⚠️ use only for parsing input of CSV/Excel files; internally we want to enforce the proper EN Locale format */
export function parseDateEnLocalePermissive(date: string): DateUtilInstance {
  const dateUtil = getDateUtil();
  return dateUtil(date, PERMISSIVE_EN_LOCALE_DATE_FORMATS_ARRAY, true);
}

export function parseDateISO(date: string): DateUtilInstance {
  const dateUtil = getDateUtil();
  return dateUtil(date, ISO_DATE_FORMAT, true);
}

export function tryParseDateISO(
  date?: string | null
): DateUtilInstance | undefined {
  return date ? parseDateISO(date) : undefined;
}

const CALENDAR_DATE_HEAD = /^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/u;

export function parseCalendarDate(
  date?: string | null
): DateUtilInstance | null {
  const day = date ? CALENDAR_DATE_HEAD.exec(date)?.[1] : undefined;
  if (!day) {
    return null;
  }
  // Strict, so an impossible day fails here rather than rolling over into March.
  const parsed = parseDateISO(day);

  return parsed.isValid() ? parsed : null;
}

export function parseDateTimeISO(dateTime: string): DateUtilInstance {
  const dateUtil = getDateUtil();

  // we use strict mode to ensure the date time is valid
  const parsedDateTime = dateUtil(
    dateTime,
    [NATIVE_DATE_ISO_DATE_TIME_FORMAT, ISO_DATE_TIME_FORMAT],
    true
  );
  if (!parsedDateTime.isValid()) {
    return parsedDateTime;
  }
  // now that we know the date time is valid,
  // we convert the string to a DateUtil object in local time
  return dateUtil(dateTime);
}

export function parseDate(date: Date): DateUtilInstance {
  const dateUtil = getDateUtil();

  const parsedDate = dateUtil(date);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isTestRunner = typeof jest !== 'undefined';
  if (!isTestRunner && !parsedDate.isValid()) {
    console.groupCollapsed('ERROR: Date object is invalid');
    console.log('provided', date);
    console.log('parsed as', parsedDate);
    console.groupEnd();
  }

  return parsedDate;
}

export function parseDateUnknown(
  // eslint-disable-next-line sonarjs/use-type-alias
  date: Date | DateUtilInstance | string
): DateUtilInstance {
  const dateUtil = getDateUtil();

  let dateString = '';
  switch (true) {
    case typeof date === 'string': {
      dateString = date;
      break;
    }
    case date instanceof Date: {
      dateString = toISOString(date);
      break;
    }
    case dateUtil.isDayjs(date): {
      dateString = date.format();
      break;
    }
  }

  const parsedDate = dateUtil(
    dateString,
    [
      NATIVE_DATE_ISO_DATE_TIME_FORMAT,
      EN_LOCALE_DATE_FORMAT,
      EN_LOCALE_DATE_SHORT_YEAR_FORMAT,
      EN_LOCALE_DATE_TIME_FORMAT,
      ISO_DATE_FORMAT,
      ISO_DATE_TIME_FORMAT,
    ],
    true
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isTestRunner = typeof jest !== 'undefined';
  if (!isTestRunner && !parsedDate.isValid()) {
    console.groupCollapsed('ERROR: date is invalid');
    console.log('provided', date);
    console.log('parsed as', dateString);
    console.groupEnd();
  }

  return parsedDate;
}

/*
 Getters
 *********************************************************/

export function getFirstDayOfMonthBeforeDate(
  date: Date | DateUtilInstance | string
) {
  const parsedDate = parseDateUnknown(date);
  return parsedDate.date() === 1
    ? parsedDate.subtract(1, 'month')
    : parsedDate.startOf('month');
}

export function getFirstDayOfMonthAfterDate(
  date: Date | DateUtilInstance | string
) {
  const parsedDate = parseDateUnknown(date);
  return parsedDate.add(1, 'month').startOf('month');
}

export function getFirstDayOfMonthEqualToOrAfterDate(
  date: Date | DateUtilInstance | string
) {
  const parsedDate = parseDateUnknown(date);
  return parsedDate.date() === 1
    ? parsedDate
    : parsedDate.startOf('month').add(1, 'month');
}

export const formatToMMMDayYear = (iso?: string | null) =>
  iso ? parseDateUnknown(iso).format('MMM D, YYYY') : '';

export const formatToMMMDay = (iso?: string | null) =>
  iso ? parseDateUnknown(iso).format('MMM D') : '';

export function getFirstDayOfMonthEqualToOrBeforeDate(
  date: Date | DateUtilInstance | string
) {
  const parsedDate = parseDateUnknown(date);
  return parsedDate.date() === 1 ? parsedDate : parsedDate.startOf('month');
}

export function getFirstDayOfYear(year: number) {
  return getNow().year(year).startOf('year');
}

export function getDaysLeft(date: string): number {
  return parseDateISO(date).diff(getToday(), 'day') + 1;
}

export function isDateOnOrBeforeToday(date?: string | null): boolean {
  if (!date) {
    return false;
  }
  return parseDateUnknown(date).isSameOrBefore(getToday());
}

/*
 Display
 *********************************************************/

export function formatDateISO(date: Date | DateUtilInstance | string): string {
  return parseDateUnknown(date).format(ISO_DATE_FORMAT);
}

export function formatMonthISO(date: Date | DateUtilInstance | string): string {
  return parseDateUnknown(date).format(ISO_MONTH_FORMAT);
}

export function tryFormatDateISO(
  date?: Date | DateUtilInstance | string | null
): string | undefined {
  return date ? formatDateISO(date) : undefined;
}

/**
 * Parses a datetime string as UTC (does not convert from local time).
 * Use this for database timestamps which are stored in UTC.
 * @param date The UTC date to parse (Date, string, or Dayjs)
 * @returns Dayjs instance in UTC mode
 */
export function parseDateTimeUtc(
  date: Date | DateUtilInstance | string
): Dayjs {
  // Handle null, undefined, and empty strings by returning invalid dayjs
  if (date === null || date === undefined || date === '') {
    return dayjs.utc('invalid');
  }
  return dayjs.utc(date);
}

// Parses a space-separated, timezone-less datetime (DATE_TIME_NO_TZ_FORMAT),
// interpreting it as UTC.
export function parseDateTimeNoTzAsUtc(value: string): DateUtilInstance {
  return getDateUtil().utc(value, DATE_TIME_NO_TZ_FORMAT, true);
}

export function formatDateTimeISOUtc(
  date: Date | DateUtilInstance | string
): string {
  return parseDateTimeUtc(date).format(NATIVE_DATE_ISO_DATE_TIME_FORMAT);
}

export function formatDateISOUtc(
  date: Date | DateUtilInstance | string
): string {
  return parseDateTimeUtc(date).format(ISO_DATE_FORMAT);
}

/**
 * @deprecated use {@link formatDateTimeISOUtc} instead
 */
export function formatDateTimeISO(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(NATIVE_DATE_ISO_DATE_TIME_FORMAT);
}

export function formatDateEnLocale(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_DATE_FORMAT);
}

export function formatDateEnLocaleNoLeadingZero(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_DATE_FORMAT_NO_LEADING_ZERO);
}

export function formatDateEnLocaleShortYear(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_DATE_SHORT_YEAR_FORMAT);
}

export function formatDateEnLocaleMonthDay(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(MONTH_DAY_EN_LOCALE_DATE_FORMAT);
}

export function formatDateEnLocaleLong(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_LONG_DATE_FORMAT);
}

export function formatDateEnLocaleLongMonthYear(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_LONG_MONTH_YEAR_FORMAT);
}

/**
 * Formats a datetime in EN locale format (MM/DD/YYYY h:mm A)
 * Interprets the input in local timezone context (does NOT convert from UTC).
 * Use this for local times like getNow() or user input.
 * For database timestamps (which are UTC), use formatDateTimeEnLocaleFromUtc instead.
 * @param date The date to format (Date, string, or Dayjs) - assumed to be in local timezone
 * @returns Formatted string in local time
 */
export function formatDateTimeEnLocale(
  date: Date | DateUtilInstance | string
): string {
  return parseDateUnknown(date).format(EN_LOCALE_DATE_TIME_FORMAT);
}

/**
 * Formats a UTC datetime to local time in EN locale format (MM/DD/YYYY h:mm A)
 * Use this for database timestamps which are always stored in UTC.
 * @param date The UTC date to format (Date, string, or Dayjs)
 * @returns Formatted string in local time
 */
export function formatDateTimeEnLocaleFromUtc(
  date: Date | DateUtilInstance | string
): string {
  return parseDateTimeUtc(date).local().format(EN_LOCALE_DATE_TIME_FORMAT);
}

export function formatDateEnLocaleRangeMMMDayYear(
  firstDate?: string | null,
  secondDate?: string | null
): string {
  const PLACEHOLDER = '*';

  if (firstDate && secondDate) {
    const first = parseDateUnknown(firstDate);
    const second = parseDateUnknown(secondDate);

    const sameYear = first.year() === second.year();

    if (sameYear) {
      return `${formatToMMMDay(firstDate)} - ${formatToMMMDayYear(secondDate)}`;
    }
    return `${formatToMMMDayYear(firstDate)} - ${formatToMMMDayYear(secondDate)}`;
  }

  if (firstDate) {
    return `${formatToMMMDayYear(firstDate)} - ${PLACEHOLDER}`;
  }

  return `${PLACEHOLDER} - ${PLACEHOLDER}`;
}

export function formatDateEnLocaleRange(
  firstDate?: Date | DateUtilInstance | string,
  secondDate?: Date | DateUtilInstance | string
): string {
  const PLACEHOLDER = '*';
  if (firstDate && secondDate) {
    return `${formatDateEnLocale(firstDate)} - ${formatDateEnLocale(
      secondDate
    )}`;
  }
  if (firstDate) {
    return `${formatDateEnLocale(firstDate)} - ${PLACEHOLDER}`;
  }
  return `${PLACEHOLDER} - ${PLACEHOLDER}`;
}

export const formatDateWithTimezone = (date: Date | string): string => {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).tz(dayjs.tz.guess()).format(ISO_DATE_TIME_FORMAT);
};

export const formatRelativeDateTime = (date: string) => {
  const parsedDate = parseDateTimeISO(date);
  const now = getNow();

  const minutesDiff = now.diff(parsedDate, 'minute');
  const hoursDiff = now.diff(parsedDate, 'hour');
  const daysDiff = now.diff(parsedDate, 'day');
  const isSameYear = now.isSame(parsedDate, 'year');

  if (minutesDiff >= 0 && minutesDiff < 1) return 'Just now';
  if (hoursDiff < 7 || (daysDiff > 1 && daysDiff <= 30))
    return parsedDate.fromNow();
  return parsedDate.calendar(null, {
    sameDay: '[Today at] h:mm A',
    lastDay: '[Yesterday at] h:mm A',
    sameElse: isSameYear ? EN_LOCALE_DATE_TIME_FORMAT : EN_LOCALE_DATE_FORMAT,
  });
};

/*
 Parse + Display
 *********************************************************/

export function getTodayISO(): string {
  return formatDateISO(getNow());
}

export function parseDateISOThenFormatEnLocale(date: string): string {
  return formatDateEnLocale(parseDateISO(date));
}

export function tryParseDateISOThenFormatEnLocale(
  date?: string | null
): string | undefined {
  return date ? parseDateISOThenFormatEnLocale(date) : undefined;
}

export function parseDateTimeISOThenFormatEnLocale(dateTime: string): string {
  return formatDateEnLocale(parseDateTimeISO(dateTime));
}

/**
 * Parses a UTC ISO datetime string and formats it to local time in EN locale format
 * Use this for database timestamps which are always stored in UTC.
 * @param dateTime ISO datetime string in UTC
 * @returns Formatted string in local time (MM/DD/YYYY h:mm A)
 */
export function parseDateTimeISOThenFormatDateTimeEnLocale(
  dateTime: string
): string {
  return formatDateTimeEnLocaleFromUtc(parseDateTimeISO(dateTime));
}

export function parseDateISOThenFormatEnLocaleShortYear(date: string): string {
  return formatDateEnLocaleShortYear(parseDateISO(date));
}

export function optionallyFormatDateTimeISO(
  // eslint-disable-next-line sonarjs/use-type-alias
  value: Date | DateUtilInstance | string | null | undefined
) {
  return value === undefined || value === null
    ? value
    : formatDateTimeISO(value);
}

/*
 Other
 *********************************************************/

// fixme: domain specific functions should reside in their bounded context
export function compareDatesUnboundedIfNull(
  firstDate: Date | DateUtilInstance | string | null | undefined,
  secondDate: Date | DateUtilInstance | string | null | undefined,
  isPeriodBeginning: boolean
): number {
  // this method compares two dates under the logic that null represents negative or positive infinity
  // isPeriodBeginning is a flag that indicates that null is negative infinity, i.e. the earliest date imaginable
  if (!firstDate && !secondDate) {
    return 0;
  }

  if (firstDate === null || firstDate === undefined) {
    return isPeriodBeginning ? -1 : 1;
  }

  if (secondDate === null || secondDate === undefined) {
    return isPeriodBeginning ? 1 : -1;
  }

  return Math.sign(
    parseDateUnknown(firstDate).diff(parseDateUnknown(secondDate))
  );
}

// fixme: domain specific functions should reside in their bounded context
export function dateOfBirthToAgeOnEnrollment(
  dateOfBirthDateISO: string
): number {
  const dob = parseDateISO(dateOfBirthDateISO);
  const startOfNextMonth = getNow().endOf('month').add(1, 'day');
  return startOfNextMonth.diff(dob, 'year');
}

// fixme: domain specific functions should reside in their bounded context
export function earliestAllowedNonDependentBirthDate(): Date {
  return getNow().startOf('day').toDate();
  // return getNow().startOf('day').subtract(18, 'years').toDate(); // TODO: https://app.clickup.com/t/869515rgk
}

// fixme: domain specific functions should reside in their bounded context
export function latestAllowedNonDependentBirthDate(): Date {
  return getNow().startOf('day').subtract(120, 'years').toDate();
}

export function getMonth(date: string) {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).format(MONTH_DATE_FORMAT);
}

export function getDayMonth(date: string) {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).format(DAY_MONTH_DATE_FORMAT);
}

export function getDayMonthYear(date: string) {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).format(DAY_MONTH_DATE_FORMAT);
}

/**
 * Subtracts business days from a date, considering holidays.
 * @param date The date to subtract from (Date, string, or Dayjs)
 * @param numDays Number of business days to subtract
 * @returns Dayjs instance
 */
export function subtractBusinessDays(date: DateInput, numDays: number): Dayjs {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).subtractBusinessDays(numDays);
}
/**
 * Adds business days to a date, considering holidays.
 * @param date The date to add to (Date, string, or Dayjs)
 * @param numDays Number of business days to add
 * @returns Dayjs instance
 */
export function addBusinessDays(date: DateInput, numDays: number): Dayjs {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).addBusinessDays(numDays);
}

export function isBusinessDay(date: DateInput): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return dayjs(date).isBusinessDay();
}

/**
 * Returns the closest business day on or before the given date.
 * If the date is already a business day, returns it as-is.
 * Otherwise returns the last business day before it.
 */
export function getClosestBusinessDayOnOrBefore(date: DateInput): Dayjs {
  // eslint-disable-next-line no-restricted-syntax
  const candidate = dayjs(date);
  if (candidate.isBusinessDay()) {
    return candidate;
  }
  return candidate.lastBusinessDay();
}

export function estimateDateOfBirth(age: number): Dayjs {
  const now = getNow().startOf('day');
  return now.subtract(age, 'year');
}

/**
 * Returns the latest (most recent) date between two date strings.
 * If only one date is provided, returns that date.
 * If neither date is provided, returns undefined.
 *
 * @param date1 - First date string (ISO format) or null/undefined
 * @param date2 - Second date string (ISO format) or null/undefined
 * @returns The latest DateUtilInstance or undefined if both are null/undefined
 */
export function getLatestDate(
  date1: string | null | undefined,
  date2: string | null | undefined
): DateUtilInstance | undefined {
  const parsedDate1 = tryParseDateISO(date1);
  const parsedDate2 = tryParseDateISO(date2);

  if (!parsedDate1 || !parsedDate2) return parsedDate1 ?? parsedDate2;

  return parsedDate1.isAfter(parsedDate2) ? parsedDate1 : parsedDate2;
}
